import { useTranslation } from "react-i18next";
import SEO from "@/components/seo/SEO";
import { GoldButton, GhostButton, Eyebrow, SectionHeading, AmbientOrbs, GlassCard } from "@/components/common/ui";
import { motion } from "framer-motion";
import { TEAM_DEPARTMENTS, ROADMAP, PARTNER_CATEGORIES } from "@/data/company";

// ---------- CAREERS ----------
export function Careers() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  return (
    <>
      <SEO title={isNl ? "Werken bij BHT" : "Careers"} path="/careers" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{isNl ? "Careers" : "Careers"}</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="mt-8 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl">
            {isNl ? "Bouw hospitality-technologie mee." : "Help us build hospitality technology."}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">
            {isNl
              ? "We hebben op dit moment geen open vacatures — maar we blijven groeien. Als u passie heeft voor hospitality en de wereld beter wilt maken voor hotelmedewerkers, laat het ons weten."
              : "We currently have no open roles — but we keep growing. If you're passionate about hospitality and want to make the world better for hotel employees, let us know."}
          </p>
        </div>
      </section>
      <section className="relative border-y border-white/[0.06] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={isNl ? "Toekomstige afdelingen" : "Future departments"} title={isNl ? "Waar we op termijn mensen zoeken." : "Where we'll be hiring."} />
          <div className="mt-12 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {TEAM_DEPARTMENTS.map((d) => (
              <div key={d.key} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-4 text-center">
                <div className="font-serif text-lg text-white">{d[lang]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-32 text-center">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <SectionHeading align="center" eyebrow={isNl ? "Open sollicitatie" : "Open application"} title={isNl ? "Wilt u meedoen?" : "Want to join us later?"} sub={isNl ? "Stuur een introductie naar careers@bhtsolutions.nl. We reageren persoonlijk." : "Send an intro to careers@bhtsolutions.nl. We reply personally."} />
          <div className="mt-10"><GoldButton href="mailto:careers@bhtsolutions.nl">careers@bhtsolutions.nl</GoldButton></div>
        </div>
      </section>
    </>
  );
}

// ---------- MEDIA ----------
export function Media() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  const sections = [
    { k: isNl ? "Bedrijfsnieuws" : "Company news", desc: isNl ? "Aankondigingen over product, team en pilots." : "Announcements about product, team and pilots." },
    { k: isNl ? "Persberichten" : "Press releases", desc: isNl ? "Officiële berichten voor pers." : "Official releases for press." },
    { k: isNl ? "Awards" : "Awards", desc: isNl ? "Erkenningen van de industrie." : "Industry recognitions." },
    { k: isNl ? "Interviews" : "Interviews", desc: isNl ? "Gesprekken met founders en team." : "Conversations with founders and team." },
    { k: isNl ? "Podcasts" : "Podcasts", desc: isNl ? "Podcast-optredens." : "Podcast appearances." },
    { k: isNl ? "Conferences" : "Conferences", desc: isNl ? "Talks op hospitality- en tech-conferenties." : "Talks at hospitality and tech conferences." },
  ];
  return (
    <>
      <SEO title={isNl ? "Media & Press" : "Media & Press"} path="/media" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{isNl ? "Media & Press" : "Media & Press"}</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="mt-8 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl">
            {isNl ? "Nieuws, aankondigingen en gesprekken." : "News, announcements and conversations."}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">
            {isNl ? "Deze sectie wordt gevoed zodra onze eerste pilots live gaan. Media-inquiries via info@bhtsolutions.nl." : "This section will fill as our first pilots go live. Media inquiries: info@bhtsolutions.nl."}
          </p>
        </div>
      </section>
      <section className="border-y border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sections.map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">M/{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 font-serif text-xl">{s.k}</div>
                <p className="mt-2 text-sm text-white/55">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ---------- INVESTORS ----------
export function Investors() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  return (
    <>
      <SEO title={isNl ? "Investor Relations" : "Investor Relations"} path="/investors" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{isNl ? "Investor Relations" : "Investor Relations"}</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="mt-8 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl">
            {isNl ? "Een Europese hospitality-technologiekans." : "A European hospitality technology opportunity."}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">
            {isNl ? "Voor institutionele investeerders en strategische partners. Voor toegang tot financiële informatie: neem contact op onder NDA." : "For institutional investors and strategic partners. For access to financial information, please reach out under NDA."}
          </p>
        </div>
      </section>
      <section className="border-y border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { k: isNl ? "Visie" : "Vision", v: isNl ? "Het compleetste Hospitality OS ter wereld." : "The world's most complete Hospitality OS." },
              { k: isNl ? "Missie" : "Mission", v: isNl ? "Meer tijd voor gasten, minder tijd voor systemen." : "More time for guests, less time on systems." },
              { k: isNl ? "Markt" : "Market", v: isNl ? "€ 40B+ hotel software TAM · groei 12%/jr." : "€ 40B+ hotel software TAM · 12%/yr growth." },
              { k: isNl ? "Positie" : "Position", v: isNl ? "AI-native · gebouwd door hoteliers." : "AI-native · built by hoteliers." },
            ].map((s, i) => (
              <GlassCard key={i} className="p-6">
                <Eyebrow>{s.k}</Eyebrow>
                <p className="mt-4 font-serif text-lg leading-tight text-white">{s.v}</p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>
      <section className="py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow="Roadmap" title={isNl ? "Waar we naartoe bouwen." : "Where we're building toward."} />
          <div className="mt-12 space-y-3">
            {ROADMAP.map((r, i) => (
              <div key={i} className="grid grid-cols-[120px_1fr] items-center gap-6 rounded-xl border border-white/[0.06] bg-white/[0.02] px-6 py-4">
                <div className="font-mono text-[11px] uppercase tracking-widest text-bht-gold">{r.quarter}</div>
                <div className="text-white/85">{r[lang]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="border-t border-white/[0.06] py-32 text-center">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <SectionHeading align="center" eyebrow={isNl ? "Praat met founders" : "Talk to the founders"} title={isNl ? "Voor investor briefings" : "For investor briefings"} sub={isNl ? "Onder NDA delen we de deck, financiële details en pilot-resultaten." : "Under NDA we share the deck, financial details and pilot results."} />
          <div className="mt-10"><GoldButton href="mailto:investors@bhtsolutions.nl">investors@bhtsolutions.nl</GoldButton></div>
        </div>
      </section>
    </>
  );
}

// ---------- PARTNERS ----------
export function Partners() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  return (
    <>
      <SEO title={isNl ? "Partners" : "Partners"} path="/partners" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{isNl ? "Partner Program" : "Partner Program"}</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="mt-8 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl">
            {isNl ? "Bouw hospitality-oplossingen met ons." : "Build hospitality solutions with us."}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">
            {isNl ? "Ons partnerprogramma is in ontwikkeling. Neem contact op om als eerste te weten wanneer we openen." : "Our partner program is in development. Get in touch to be first to know when we open it."}
          </p>
        </div>
      </section>
      <section className="border-y border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={isNl ? "Categorieën" : "Categories"} title={isNl ? "Voor wie we ons openstellen." : "Who we open the doors for."} />
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {PARTNER_CATEGORIES.map((c, i) => (
              <div key={c.key} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">P/{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 font-serif text-xl">{c[lang]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-32 text-center">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <SectionHeading align="center" eyebrow="Partners" title={isNl ? "Klaar om samen te bouwen?" : "Ready to build together?"} />
          <div className="mt-10"><GoldButton href="mailto:partners@bhtsolutions.nl">partners@bhtsolutions.nl</GoldButton></div>
        </div>
      </section>
    </>
  );
}

// ---------- COMMUNITY ----------
export function Community() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  const items = [
    { k: isNl ? "Events" : "Events", v: isNl ? "Kleine, intieme hospitality-events voor onze klanten en netwerk." : "Small, intimate hospitality events for our customers and network." },
    { k: isNl ? "Webinars" : "Webinars", v: isNl ? "Maandelijkse deep-dives over specifieke workflows en use-cases." : "Monthly deep-dives on specific workflows and use cases." },
    { k: isNl ? "Kennis-sessies" : "Knowledge sessions", v: isNl ? "Cross-property learnings van hoteliers, gedeeld met peers." : "Cross-property learnings from hoteliers, shared with peers." },
    { k: isNl ? "Hospitality Academy" : "Hospitality Academy", v: isNl ? "Trainingsprogramma voor uw team op BHT One en moderne operatie." : "Training programme for your team on BHT One and modern operations." },
    { k: isNl ? "User Groups" : "User Groups", v: isNl ? "Regionale gebruikersgroepen — Nederland, Duitsland, UK, Zuid-Europa." : "Regional user groups — NL, DE, UK, Southern Europe." },
    { k: isNl ? "Release Notes" : "Release Notes", v: isNl ? "Wekelijkse product-updates — kort en scherp." : "Weekly product updates — short and sharp." },
  ];
  return (
    <>
      <SEO title={isNl ? "Community" : "Community"} path="/community" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>{isNl ? "Community" : "Community"}</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="mt-8 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl">
            {isNl ? "Een community van hoteliers die de industrie beter maken." : "A community of hoteliers making the industry better."}
          </motion.h1>
          <p className="mt-8 max-w-2xl text-lg text-white/65">
            {isNl ? "Onze community is in opbouw. Meld u aan voor de nieuwsbrief en u bent als eerste op de hoogte." : "Our community is being built. Subscribe to the newsletter and you'll be the first to know."}
          </p>
        </div>
      </section>
      <section className="border-y border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((s, i) => (
              <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-bht-gold">C/{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 font-serif text-xl">{s.k}</div>
                <p className="mt-2 text-sm text-white/55">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
